import { useEffect, useRef, useState } from 'react'
import {
  archiveLesson,
  errorMessage,
  publishLesson,
  scheduleLesson,
} from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { LEVELS, LEVEL_FA, STATUS_FA, WEEK_DAYS } from './constants'
import { formatDatetime, isoToDateKey, todayKey } from './dates'
import { archiveQuestion, confirmed, publishQuestion } from './confirm'
import { lessonName } from './lessonTitle'
import {
  PHASE_CHIP_CLS,
  PHASE_DOT_CLS,
  PHASE_FA,
  WEEKDAY_NAMES_FA,
  WEEKEND_COLS,
  bucketByDay,
  dayLabel,
  lessonPhase,
  monthCells,
  monthName,
  monthOf,
  monthSummary,
  publishBlocker,
  retimeToDay,
  shiftMonth,
  timeOfDay,
} from './calendarLogic'
import { SchedulePicker } from './components/SchedulePicker'
import { EditLessonForm } from './components/EditLessonForm'
import { CreateForm } from './components/CreateForm'
import { AutoDraftForm } from './components/AutoDraftForm'

// ─── Publishing calendar ──────────────────────────────────────────────────────
//
// A Gregorian, Monday-first month grid over the lessons' `scheduledPublishAt`. Scheduling a
// draft plans it; the host's `lessons-publish-scheduled` timer publishes it
// at that time if it is ready. The calendar's job is to make the plan visible
// (what is due, what is overdue — i.e. not ready or not yet picked up — what
// cannot go out yet and why) and to let the admin act on it from here: drag
// to reschedule, publish now, archive, edit, add lessons.
//
// Colours come from the theme tokens (surface / line / ink / brand / good /
// warn / bad and their tints), never from fixed light-only shades: the admin
// panel is shown in the dark theme at night too, and `bg-white` / `*-100`
// utilities do not follow it.

const CHIPS_PER_CELL = 3
const STATUS_FILTERS = ['all', 'draft', 'published', 'archived']
const isWeekend = col => WEEKEND_COLS.includes(col)

const CARD = 'rounded-2xl border border-line bg-surface shadow-sm'
const FIELD = 'rounded-lg border border-line-2 bg-surface px-2 py-1 text-xs text-ink focus:border-brand-500 focus:outline-none'
const BTN = 'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40'
const BTN_PRIMARY = `${BTN} bg-brand-600 text-on-ink hover:bg-brand-700`
const BTN_GOOD = `${BTN} bg-good text-on-ink hover:opacity-90`
const BTN_OUTLINE = `${BTN} border border-line-2 font-medium text-ink-2 hover:bg-surface-2`

function PhaseChip({ phase }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PHASE_CHIP_CLS[phase]}`}>{PHASE_FA[phase]}</span>
}

/** A dialog over the page: the create forms are long, and opened inline they land above the fold where a click looks like it did nothing. */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-black/50 p-2 sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="mx-auto my-2 max-w-3xl rounded-2xl bg-ground p-3 shadow-xl sm:my-6 sm:p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-ink">{title}</p>
          <button type="button" onClick={onClose} aria-label="بستن"
            className="rounded-lg px-2.5 py-1 text-ink-2 hover:bg-surface-2">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** One lesson's actions inside the day panel and the overdue list. */
function LessonActions({ lesson, onUpdate, onEdit }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const blocker = publishBlocker(lesson)

  async function run(action) {
    setBusy(true); setErr('')
    try { onUpdate(await action()) }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false) }
  }

  function publish() {
    if (!confirmed(publishQuestion(lesson))) return
    run(() => publishLesson(lesson.id))
  }

  function archive() {
    if (!confirmed(archiveQuestion(lesson))) return
    run(() => archiveLesson(lesson.id))
  }

  return (
    <div className="space-y-2">
      <SchedulePicker lesson={lesson} onUpdate={onUpdate} />
      <div className="flex gap-2">
        {onEdit && <button type="button" onClick={onEdit} className={`${BTN_OUTLINE} flex-1`}>ویرایش</button>}
        {lesson.status === 'published' && (
          <button type="button" disabled={busy} onClick={archive} className={`${BTN_OUTLINE} flex-1`}>
            بایگانی
          </button>
        )}
        {lesson.status === 'draft' && !blocker && (
          <button type="button" disabled={busy} onClick={publish} className={`${BTN_GOOD} flex-1`}>
            {busy ? '…' : 'انتشار اکنون'}
          </button>
        )}
      </div>
      {lesson.status === 'draft' && blocker && (
        <p className="rounded-lg bg-warn-tint px-2.5 py-1.5 text-xs text-warn-ink">آمادهٔ انتشار نیست: {blocker}</p>
      )}
      {err && <p className="text-xs text-bad-ink">{err}</p>}
    </div>
  )
}

/** Schedule one of the drafts onto the selected day, at a chosen time. */
function ScheduleDraftOnDay({ dayKey, drafts, onUpdate }) {
  const [lessonId, setLessonId] = useState('')
  const [time, setTime] = useState('09:00')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function schedule() {
    if (!lessonId) return
    setBusy(true); setErr('')
    try {
      onUpdate(await scheduleLesson(lessonId, retimeToDay(dayKey, null, time)))
      setLessonId('')
    } catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false) }
  }

  if (drafts.length === 0) {
    return <p className="border-t border-line px-4 py-3 text-center text-xs text-ink-3">پیش‌نویس بدون تاریخی برای زمان‌بندی نمانده است.</p>
  }

  return (
    <div className="space-y-2 border-t border-line px-4 py-3">
      <p className="text-xs font-semibold text-ink-2">زمان‌بندی یک پیش‌نویس برای این روز</p>
      <select value={lessonId} onChange={e => setLessonId(e.target.value)} className={`${FIELD} w-full`}>
        <option value="">یک پیش‌نویس را انتخاب کنید…</option>
        {drafts.map(l => <option key={l.id} value={l.id}>{lessonName(l)}</option>)}
      </select>
      <div className="flex items-center gap-2">
        <label className="text-xs text-ink-2" htmlFor="cal-time">ساعت</label>
        <input id="cal-time" type="time" value={time} onChange={e => setTime(e.target.value)} className={FIELD} />
        <button type="button" onClick={schedule} disabled={busy || !lessonId || !time} className={`${BTN_PRIMARY} flex-1`}>
          {busy ? '…' : 'زمان‌بندی'}
        </button>
      </div>
      {err && <p className="text-xs text-bad-ink">{err}</p>}
    </div>
  )
}

/** A draft with no date: drag it onto a day, or pick a moment here. */
function UnscheduledRow({ lesson, onUpdate }) {
  const [val, setVal] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const blocker = publishBlocker(lesson)

  async function save() {
    if (!val) return
    setBusy(true); setErr('')
    try { onUpdate(await scheduleLesson(lesson.id, new Date(val).toISOString())); setVal('') }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-1 px-4 py-2.5"
      draggable onDragStart={e => e.dataTransfer.setData('text/lesson-id', lesson.id)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="cursor-grab text-ink-3" title="برای زمان‌بندی روی یک روز بکش">⠿</span>
        <span className="min-w-0 flex-1 truncate text-sm text-ink" dir="auto">{lessonName(lesson)}</span>
        <span className="text-xs text-ink-3">{LEVEL_FA[lesson.level]}</span>
        {blocker && <span className="text-xs text-warn-ink" title={blocker}>⚠ {blocker}</span>}
        <input type="datetime-local" value={val} onChange={e => setVal(e.target.value)}
          aria-label={`زمان انتشار ${lessonName(lesson)}`} className={FIELD} />
        <button type="button" onClick={save} disabled={busy || !val} className={BTN_PRIMARY}>
          {busy ? '…' : 'زمان‌بندی'}
        </button>
      </div>
      {err && <p className="text-xs text-bad-ink">{err}</p>}
    </div>
  )
}

export function CalendarView({ lessons, onUpdate, onCreated = onUpdate }) {
  const [view, setView] = useState(() => monthOf(new Date()))
  const [selectedKey, setSelectedKey] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [dragOverKey, setDragOverKey] = useState(null)
  const [dropErr, setDropErr] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [levelFilter, setLevelFilter] = useState('all')
  // `{ mode: 'manual' | 'upload', dayKey: string | null }` while a new lesson is being written
  const [creating, setCreating] = useState(null)
  const [createErr, setCreateErr] = useState('')
  const panelRef = useRef(null)

  // On a phone the day panel sits below the grid, out of sight: bring it up when a day is picked.
  useEffect(() => {
    if (selectedKey && window.matchMedia?.('(max-width: 1023px)').matches) {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedKey])

  const now = new Date()
  const byDay = bucketByDay(lessons, { status: statusFilter, level: levelFilter })
  const unscheduled = lessons.filter(l => l.status === 'draft' && !l.scheduledPublishAt)
  const overdue = lessons
    .filter(l => lessonPhase(l, now) === 'overdue')
    .sort((a, b) => new Date(a.scheduledPublishAt) - new Date(b.scheduledPublishAt))

  const cells = monthCells(view.year, view.month)
  const summary = monthSummary(cells, byDay, now)
  const tk = todayKey()
  const selectedLessons = selectedKey ? (byDay[selectedKey] ?? []) : []
  const thisMonth = monthOf(now)
  const viewingToday = view.year === thisMonth.year && view.month === thisMonth.month

  function go(next) {
    setView(next)
    setSelectedKey(null)
    setEditingId(null)
  }

  function mergeUpdate(updated) {
    onUpdate(updated)
    // The lesson may have moved to another day; keep the panel on it
    if (selectedKey && updated.scheduledPublishAt) {
      const key = isoToDateKey(updated.scheduledPublishAt)
      if (key !== selectedKey) setSelectedKey(key)
    }
  }

  function startCreate(mode, dayKey) {
    setCreateErr('')
    setCreating({ mode, dayKey })
  }

  // A lesson made here is planned for the day it was made on (09:00), so it
  // lands on the grid straight away; if planning fails it is still created.
  async function created(lesson) {
    const dayKey = creating?.dayKey
    setCreating(null)
    if (!dayKey) { onCreated(lesson); return }
    try {
      onCreated(await scheduleLesson(lesson.id, retimeToDay(dayKey, null)))
      setSelectedKey(dayKey)
    } catch (ex) {
      onCreated(lesson)
      setCreateErr(`درس ساخته شد ولی زمان‌بندی نشد: ${errorMessage(ex)}`)
    }
  }

  async function dropOnDay(e, dayKey) {
    e.preventDefault()
    setDragOverKey(null)
    const lesson = lessons.find(l => l.id === e.dataTransfer.getData('text/lesson-id'))
    if (!lesson || lesson.status !== 'draft') return
    setDropErr('')
    try { mergeUpdate(await scheduleLesson(lesson.id, retimeToDay(dayKey, lesson.scheduledPublishAt))) }
    catch (ex) { setDropErr(errorMessage(ex)) }
  }

  const yearChoices = Array.from({ length: 6 }, (_, i) => view.year - 2 + i)

  return (
    <div className="space-y-4">
      {/* Month navigation */}
      <div className={`${CARD} flex flex-wrap items-center gap-2 px-4 py-3`}>
        <button type="button" onClick={() => go(shiftMonth(view, -1))} aria-label="ماه قبل"
          className="rounded-lg px-3 py-1.5 text-lg leading-none text-ink-2 hover:bg-surface-2">›</button>
        <button type="button" onClick={() => go(shiftMonth(view, 1))} aria-label="ماه بعد"
          className="rounded-lg px-3 py-1.5 text-lg leading-none text-ink-2 hover:bg-surface-2">‹</button>
        <h2 className="min-w-[6rem] text-lg font-bold text-ink">
          {monthName(view.month)} {faDigits(view.year)}
        </h2>
        <select value={view.month} onChange={e => go({ year: view.year, month: Number(e.target.value) })}
          aria-label="ماه" className={FIELD}>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i} value={i + 1}>{monthName(i + 1)}</option>
          ))}
        </select>
        <select value={view.year} onChange={e => go({ year: Number(e.target.value), month: view.month })}
          aria-label="سال" className={FIELD}>
          {yearChoices.map(y => <option key={y} value={y}>{faDigits(y)}</option>)}
        </select>
        <button type="button" onClick={() => go(thisMonth)} disabled={viewingToday} className={BTN_OUTLINE}>
          امروز
        </button>

        <div className="ms-auto flex flex-wrap items-center gap-2">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            aria-label="فیلتر وضعیت" className={FIELD}>
            {STATUS_FILTERS.map(s => <option key={s} value={s}>{s === 'all' ? 'همهٔ وضعیت‌ها' : STATUS_FA[s]}</option>)}
          </select>
          <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
            aria-label="فیلتر سطح" className={FIELD}>
            <option value="all">همهٔ سطح‌ها</option>
            {LEVELS.map(l => <option key={l} value={l}>{LEVEL_FA[l]}</option>)}
          </select>
          <button type="button" onClick={() => startCreate('upload', selectedKey)} className={BTN_OUTLINE}>
            + درس از فایل
          </button>
          <button type="button" onClick={() => startCreate('manual', selectedKey)} className={BTN_PRIMARY}>
            + درس جدید
          </button>
        </div>
      </div>

      {createErr && <div className="rounded-xl bg-warn-tint px-4 py-3 text-sm text-warn-ink">{createErr}</div>}

      {/* New lesson, planned for the selected day (or left undated when none is selected) */}
      {creating && (
        <Modal
          onClose={() => setCreating(null)}
          title={creating.dayKey
            ? `${creating.mode === 'manual' ? 'درس جدید' : 'درس از فایل'} برای ${dayLabel(creating.dayKey)} (ساعت ۹ صبح؛ بعداً قابل تغییر)`
            : `${creating.mode === 'manual' ? 'درس جدید' : 'درس از فایل'} بدون تاریخ؛ بعد از ساخت روی یک روز بکشید`}
        >
          {creating.mode === 'manual'
            ? <CreateForm onCreated={created} onCancel={() => setCreating(null)} />
            : <AutoDraftForm onCreated={created} onCancel={() => setCreating(null)} />}
        </Modal>
      )}

      {/* This month at a glance */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className={`rounded-full px-3 py-1 font-semibold ${PHASE_CHIP_CLS.scheduled}`}>
          {faDigits(summary.scheduled)} زمان‌بندی‌شده
        </span>
        <span className={`rounded-full px-3 py-1 font-semibold ${PHASE_CHIP_CLS.published}`}>
          {faDigits(summary.published)} منتشرشده
        </span>
        <span className={`rounded-full px-3 py-1 font-semibold ${summary.overdue ? PHASE_CHIP_CLS.overdue : PHASE_CHIP_CLS.unscheduled}`}>
          {faDigits(summary.overdue)} عقب‌افتاده
        </span>
      </div>

      {/* Planned time passed, still a draft */}
      {overdue.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-bad/30 bg-surface shadow-sm">
          <div className="bg-bad-tint px-4 py-3">
            <p className="text-sm font-bold text-bad-ink">
              {faDigits(overdue.length)} درس موعدش گذشته ولی هنوز منتشر نشده
            </p>
            <p className="mt-0.5 text-xs text-ink-2">
              انتشار خودکار فقط درس‌های آماده را منتشر می‌کند؛ این‌ها یا آماده نیستند یا هنوز نوبتشان نرسیده. با «انتشار اکنون» هم می‌توانید منتشرشان کنید.
            </p>
          </div>
          <div className="divide-y divide-line">
            {overdue.map(l => (
              <div key={l.id} className="space-y-2 px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink" dir="auto">{lessonName(l)}</span>
                  <button type="button" onClick={() => go(monthOf(new Date(l.scheduledPublishAt)))}
                    className="text-xs text-bad-ink underline">
                    {formatDatetime(l.scheduledPublishAt)}
                  </button>
                </div>
                <LessonActions lesson={l} onUpdate={mergeUpdate} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Calendar grid */}
        <div className={`${CARD} min-w-0 flex-1 overflow-hidden`}>
          <div className="grid grid-cols-7 border-b border-line bg-surface-2/60">
            {WEEK_DAYS.map((d, col) => (
              <div key={d} title={WEEKDAY_NAMES_FA[col]}
                className={`py-2.5 text-center text-xs font-bold ${isWeekend(col) ? 'text-bad' : 'text-ink-2'}`}>
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {cells.map((cell, i) => {
              if (!cell) return <div key={`e${i}`} className="min-h-[3.75rem] border-b border-s border-line bg-surface-2/30 sm:min-h-[6rem]" />
              const dayLessons = byDay[cell.key] ?? []
              const isToday = cell.key === tk
              const isSelected = cell.key === selectedKey
              const isDragOver = cell.key === dragOverKey
              const select = () => { setSelectedKey(isSelected ? null : cell.key); setEditingId(null) }
              const tone = isDragOver
                ? 'bg-brand-100 ring-2 ring-inset ring-brand-500'
                : isSelected
                  ? 'bg-brand-50 ring-2 ring-inset ring-brand-500'
                  : isWeekend(cell.col) ? 'bg-muted-tint hover:bg-surface-2' : 'hover:bg-surface-2'
              return (
                <div
                  key={cell.key}
                  role="button"
                  tabIndex={0}
                  aria-label={`${faDigits(cell.day)} ${monthName(view.month)}، ${faDigits(dayLessons.length)} درس`}
                  aria-pressed={isSelected}
                  onClick={select}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select() } }}
                  onDragOver={e => { e.preventDefault(); if (dragOverKey !== cell.key) setDragOverKey(cell.key) }}
                  onDragLeave={() => setDragOverKey(k => (k === cell.key ? null : k))}
                  onDrop={e => dropOnDay(e, cell.key)}
                  className={`flex min-h-[3.75rem] cursor-pointer flex-col gap-1 border-b border-s border-line p-1 transition-colors sm:min-h-[6rem] sm:p-1.5 ${tone}`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`text-sm font-semibold ${isToday
                      ? 'inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-brand-600 px-1 text-xs text-on-ink'
                      : isWeekend(cell.col) ? 'text-bad' : 'text-ink'}`}>
                      {faDigits(cell.day)}
                    </span>
                  </div>
                  {dayLessons.length > 0 && (
                    <div className="flex flex-wrap gap-0.5 sm:hidden" aria-hidden="true">
                      {dayLessons.slice(0, 6).map(l => (
                        <span key={l.id} className={`h-1.5 w-1.5 rounded-full ${PHASE_DOT_CLS[lessonPhase(l, now)]}`} />
                      ))}
                    </div>
                  )}
                  {dayLessons.slice(0, CHIPS_PER_CELL).map(l => {
                    const phase = lessonPhase(l, now)
                    return (
                      <div key={l.id}
                        title={`${lessonName(l)} — ${PHASE_FA[phase]} — ${formatDatetime(l.scheduledPublishAt)}`}
                        draggable={l.status === 'draft'}
                        onDragStart={e => e.dataTransfer.setData('text/lesson-id', l.id)}
                        className={`hidden truncate rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4 sm:block ${PHASE_CHIP_CLS[phase]}`}>
                        {lessonName(l)}
                      </div>
                    )
                  })}
                  {dayLessons.length > CHIPS_PER_CELL && (
                    <span className="hidden text-[11px] text-ink-3 sm:inline">+{faDigits(dayLessons.length - CHIPS_PER_CELL)} دیگر</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Day detail panel — wider while editing, so the form has room */}
        {selectedKey && (
          <div ref={panelRef} className={`${CARD} shrink-0 scroll-mt-24 self-start overflow-hidden lg:transition-[width] ${editingId ? 'lg:w-[28rem]' : 'lg:w-80'}`}>
            <div className="bg-brand-50 px-4 py-3">
              <p className="text-sm font-bold text-brand-800">{dayLabel(selectedKey)}</p>
              <p className="mt-0.5 text-xs text-ink-2">
                {selectedLessons.length === 0 ? 'درسی برای این روز برنامه‌ریزی نشده' : `${faDigits(selectedLessons.length)} درس در این روز`}
              </p>
            </div>
            {dropErr && <p className="border-b border-line px-4 py-2 text-xs text-bad-ink">{dropErr}</p>}
            <div className="divide-y divide-line">
              {selectedLessons.map(l => {
                const phase = lessonPhase(l, now)
                return (
                  <div key={l.id} className="space-y-2 px-4 py-3"
                    draggable={l.status === 'draft'} onDragStart={e => e.dataTransfer.setData('text/lesson-id', l.id)}>
                    <div className="flex items-center gap-2">
                      {l.status === 'draft' && <span className="cursor-grab text-ink-3" title="برای جابه‌جایی بکش">⠿</span>}
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink" dir="auto">{lessonName(l)}</span>
                      <PhaseChip phase={phase} />
                    </div>
                    <p className="text-xs text-ink-2">{l.titleEn} · {LEVEL_FA[l.level]} · ساعت {faDigits(timeOfDay(l.scheduledPublishAt))}</p>

                    {editingId === l.id ? (
                      <EditLessonForm lesson={l} onSaved={u => { mergeUpdate(u); setEditingId(null) }} onCancel={() => setEditingId(null)} />
                    ) : (
                      <LessonActions lesson={l} onUpdate={mergeUpdate} onEdit={() => setEditingId(l.id)} />
                    )}
                  </div>
                )
              })}
            </div>
            <div className="flex gap-2 border-t border-line px-4 py-3">
              <button type="button" onClick={() => startCreate('manual', selectedKey)} className={`${BTN_PRIMARY} flex-1`}>
                + درس جدید در این روز
              </button>
              <button type="button" onClick={() => startCreate('upload', selectedKey)} className={BTN_OUTLINE}>
                از فایل
              </button>
            </div>
            <ScheduleDraftOnDay dayKey={selectedKey} drafts={unscheduled} onUpdate={mergeUpdate} />
          </div>
        )}
      </div>

      {/* Unscheduled drafts */}
      {unscheduled.length > 0 && (
        <div className={CARD}>
          <div className="border-b border-line px-4 py-3">
            <p className="text-sm font-bold text-ink">درس‌های پیش‌نویس بدون تاریخ ({faDigits(unscheduled.length)})</p>
            <p className="text-xs text-ink-2">روی یک روز تقویم بکشید یا زمان را همین‌جا بزنید.</p>
          </div>
          <div className="divide-y divide-line">
            {unscheduled.map(l => <UnscheduledRow key={l.id} lesson={l} onUpdate={mergeUpdate} />)}
          </div>
        </div>
      )}
    </div>
  )
}

/** The calendar tab (X1): the view as it was, fed from the shell's props bag. */
export function CalendarTab({ lessons, onUpdate, onCreated }) {
  return <CalendarView lessons={lessons} onUpdate={onUpdate} onCreated={onCreated} />
}
